import React, { useCallback, useContext, useState, useMemo } from "react";
import { ModuleWrapper } from "components/fragments";
import { HeaderA } from "components/headers";
import { Pill, Text, DataTable, ButtonLink, Icon } from "components/commons";
import { useApi, useFilter, useMount } from "hooks";
import { mapObject, pluralize } from "services";
import { Path } from "paths";
import { PillType } from "enums";
import columns from "./columns";
import lang from "translations";
import { formatNumberToMoneyWithCurrencySymbol } from "services/money.service";
import { VenueContext } from "contexts";
import MpBalanceMovementInFilter from "./mp-balance-movement-in-filter";
import { balanceMovementListFilterState } from "./filters";
import {
  balanceMovementInListFilterRequest,
  balanceMovementInListResponse,
} from "mappers/multi-property.mapper";
import {
  searchBalanceMovementsIn,
  searchBalanceMovementsInReportExport,
} from "apis/multi-property.api";
import { mixpanel, TrackEvent } from "mixpanel";
import useFilterStore, { filterName } from "hooks/filterStore";
import { isEmpty } from "lodash";

const MpBalanceMovementInList = () => {
  const { venue } = useContext(VenueContext);
  const { setState: setFilterState, getState } = useFilterStore();

  const {
    request: searchBalanceMovementsInRequest,
    error,
    mappedData,
    result: searchBalanceMovementsInResult = {
      data: [],
      metadata: { total: 0, totalCredits: 0, totalFreeCredits: 0 },
    },
    loading,
  } = useApi({
    api: searchBalanceMovementsIn,
    isArray: true,
    handleOwnError: true,
    mapper: balanceMovementInListResponse,
  });

  const { modifyFilter, clearFilter, filterState, requestState, modifyFilters, isFilterDirty } =
    useFilter(
      filterName.balanceMovementIn === getState().name && !isEmpty(getState().filter)
        ? getState().filter
        : balanceMovementListFilterState(venue),
      balanceMovementListFilterState(venue)
    );

  const fetchBalanceMovementsIn = useCallback(
    async (requestState) => {
      await searchBalanceMovementsInRequest(
        mapObject(requestState, balanceMovementInListFilterRequest)
      );
    },
    [searchBalanceMovementsInRequest]
  );

  const fetchUpdateStore = useCallback(
    async (requestState, filterState) => {
      setFilterState({
        name: filterName.balanceMovementIn,
        filter: filterState,
      });
      fetchBalanceMovementsIn(requestState);
    },
    [fetchBalanceMovementsIn, setFilterState]
  );

  useMount(() => {
    if (filterName.balanceMovementIn !== getState().name)
      setFilterState({
        name: filterName.balanceMovementIn,
        filter: {},
      });
    fetchBalanceMovementsIn(requestState);
    mixpanel.track(TrackEvent.VisitedPage, {
      Page: lang.balanceMovementsInPage,
    });
  });

  const [hasAppliedFilter, setHasAppliedFilter] = useState(isFilterDirty);

  const renderGuest = useCallback((name, tagUid, tagUseCount, guestId) => {
    let guestTagDisplay = "";

    guestTagDisplay = (
      <div className="flex items-center">
        <Text className="mr-sm group-hover:text-black-light" tagUid>
          {tagUid}
        </Text>
        <Pill type={PillType.Blueish} size="text-xs">
          {tagUseCount} use
        </Pill>
      </div>
    );

    return (
      <ButtonLink
        className="grid grid-cols-1 cursor-pointer group"
        path={Path.GUEST_DETAILS_ID(guestId)}
      >
        {name ? (
          <Text color="text-left text-pelorous group-hover:text-pelorous-dark">{name}</Text>
        ) : (
          <Text color="text-left text-pelorous group-hover:text-pelorous-dark">
            {[lang.guest, " ", guestId]}
          </Text>
        )}
        <div>{guestTagDisplay}</div>
      </ButtonLink>
    );
  }, []);

  const renderDateTime = useCallback((checkInDate, checkInTime) => {
    return (
      <div>
        <Text>{checkInDate}</Text>
        <Text color="text-gray">{checkInTime}</Text>
      </div>
    );
  }, []);

  const prepareBalanceMovementsInList = useCallback(() => {
    const movements = mappedData.map((balanceMovementIn) => {
      const {
        previousVenueName,
        guestId,
        previousGuestId,
        name,
        tagUid,
        tagUseCount,
        checkInDate,
        checkInTime,
        checkOutDate,
        checkOutTime,
        multiPropertyCreditsIn,
        multiPropertyFreeCreditsIn,
      } = balanceMovementIn;

      return {
        previousVenueName,
        guestId,
        previousGuestId,
        name: renderGuest(name, tagUid, tagUseCount, guestId),
        checkInDateTime: renderDateTime(checkInDate, checkInTime),
        checkOutDateTime: renderDateTime(checkOutDate, checkOutTime),
        multiPropertyCreditsIn,
        multiPropertyFreeCreditsIn,
      };
    });

    if (movements.length > 0) {
      movements.unshift({
        previousVenueName: lang.totals,
        guestId: "",
        previousGuestId: "",
        name: "",
        checkInDateTime: "",
        checkOutDateTime: "",
        multiPropertyCreditsIn: formatNumberToMoneyWithCurrencySymbol(
          searchBalanceMovementsInResult.metadata.totalCredits
        ),
        multiPropertyFreeCreditsIn: formatNumberToMoneyWithCurrencySymbol(
          searchBalanceMovementsInResult.metadata.totalFreeCredits
        ),
      });
    }

    return movements;
  }, [mappedData, renderGuest, renderDateTime, searchBalanceMovementsInResult]);

  const balanceMovementsIn = useMemo(() => {
    return prepareBalanceMovementsInList();
  }, [prepareBalanceMovementsInList]);

  const applyFilterCb = useCallback(
    async (searchKey) => {
      const { requestState, filterState } = modifyFilters({
        page: 1,
        searchKey,
      });
      await fetchUpdateStore(requestState, filterState);
      setHasAppliedFilter(true);
    },
    [fetchUpdateStore, modifyFilters]
  );

  const clearFilterCb = useCallback(() => {
    const { requestState, filterState } = clearFilter();
    fetchUpdateStore(requestState, filterState);
    setHasAppliedFilter(false);
  }, [clearFilter, fetchUpdateStore]);

  const balanceMovementsInResultContent = useMemo(() => {
    if (hasAppliedFilter) {
      return `${searchBalanceMovementsInResult.metadata.total} ${pluralize(
        searchBalanceMovementsInResult.metadata.total,
        lang.searchResult,
        lang.searchResults
      )}`;
    }
    return null;
  }, [searchBalanceMovementsInResult.metadata.total, hasAppliedFilter]);

  const {
    request: searchBalanceMovementsInReportExportRequest,
    loading: searchBalanceMovementsInReportExportLoading,
  } = useApi({
    api: searchBalanceMovementsInReportExport,
  });

  const exportXlsx = useCallback(() => {
    searchBalanceMovementsInReportExportRequest(
      mapObject({ ...requestState }, balanceMovementInListFilterRequest)
    );
  }, [searchBalanceMovementsInReportExportRequest, requestState]);

  return (
    <ModuleWrapper
      header={
        <HeaderA
          title={lang.balanceMovementsIn}
          description={lang.balanceMovementsDescription}
          button={{
            iconPrefix: <Icon className="mr-sm" name="download" paddingless fontSize={12} />,
            text: lang.exportXlsx,
            loading: searchBalanceMovementsInReportExportLoading,
            disabled: searchBalanceMovementsInReportExportLoading,
            onClick: () => {
              exportXlsx();
            },
          }}
          className="mb-md"
        />
      }
    >
      <>
        <MpBalanceMovementInFilter
          filterState={filterState}
          requestState={requestState}
          modifyFilter={modifyFilter}
          modifyFilters={modifyFilters}
          applyFilter={applyFilterCb}
          clearFilter={clearFilterCb}
        />
        <DataTable
          resultContent={balanceMovementsInResultContent}
          page={filterState.page}
          pageSize={filterState.pageSize}
          onChangePage={modifyFilters}
          fetchList={fetchUpdateStore}
          total={searchBalanceMovementsInResult.metadata.total}
          loading={loading}
          columns={columns}
          data={balanceMovementsIn}
          error={error}
          minWidth="800px"
          hasAppliedFilter={isFilterDirty && hasAppliedFilter}
        />
      </>
    </ModuleWrapper>
  );
};

export default MpBalanceMovementInList;
